import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  AlertTriangle, 
  CheckCircle2,
  Clock,
  MapPin,
  ChevronRight,
  Bell,
  Banknote,
  UserX,
  Ban,
  Users,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const alertTypeConfig = {
  urgent_till_alert: {
    icon: AlertTriangle,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    iconBgRead: 'bg-slate-100',
    iconColorRead: 'text-slate-500',
    category: 'till',
  },
  penalty: {
    icon: Banknote,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    iconBgRead: 'bg-slate-100',
    iconColorRead: 'text-slate-500',
    category: 'penalty',
  },
  worker_cancelled: {
    icon: Ban,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    iconBgRead: 'bg-slate-100',
    iconColorRead: 'text-slate-500',
    category: 'penalty',
  },
  no_show: {
    icon: UserX,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    iconBgRead: 'bg-slate-100',
    iconColorRead: 'text-slate-500',
    category: 'penalty',
  },
};

const penaltyStatusStyles = {
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-800',
  waived: 'bg-slate-100 text-slate-600',
};

export default function AlertsPage() {
  const { api } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unread');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await api.get('/notifications/urgent-alerts');
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleMarkRead = async (alertId) => {
    try {
      await api.patch(`/notifications/${alertId}/read`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, isRead: true } : a));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const filteredAlerts = alerts.filter(a => {
    const readMatch = filter === 'unread' ? !a.isRead : a.isRead;
    if (!readMatch) return false;
    if (categoryFilter === 'all') return true;
    const config = alertTypeConfig[a.type];
    return config?.category === categoryFilter;
  });

  const unreadCount = alerts.filter(a => !a.isRead).length;
  const tillCount = alerts.filter(a => alertTypeConfig[a.type]?.category === 'till' && !a.isRead).length;
  const penaltyCount = alerts.filter(a => alertTypeConfig[a.type]?.category === 'penalty' && !a.isRead).length;

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-12 w-full" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full h-11 w-11 min-w-[44px] shrink-0" onClick={() => navigate('/manager')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-mgr-alerts' : undefined}>Alerts</h1>
            <p className="text-slate-500 mt-1">Till discrepancies, penalties & accountability events</p>
          </div>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-red-500 text-white text-lg px-3 py-1" data-testid="unread-count-badge">
            {unreadCount} unread
          </Badge>
        )}
      </div>

      {/* Category filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          variant={categoryFilter === 'all' ? 'default' : 'outline'}
          className={`rounded-full text-xs h-11 ${categoryFilter === 'all' ? 'bg-[#0f1b3d]' : ''}`}
          onClick={() => setCategoryFilter('all')}
          data-testid="filter-all"
        >
          All ({unreadCount})
        </Button>
        <Button
          size="sm"
          variant={categoryFilter === 'till' ? 'default' : 'outline'}
          className={`rounded-full text-xs h-11 ${categoryFilter === 'till' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
          onClick={() => setCategoryFilter('till')}
          data-testid="filter-till"
        >
          <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Till Issues {tillCount > 0 && `(${tillCount})`}
        </Button>
        <Button
          size="sm"
          variant={categoryFilter === 'penalty' ? 'default' : 'outline'}
          className={`rounded-full text-xs h-11 ${categoryFilter === 'penalty' ? 'bg-amber-600 hover:bg-amber-700' : 'text-amber-700 border-amber-200 hover:bg-amber-50'}`}
          onClick={() => setCategoryFilter('penalty')}
          data-testid="filter-penalty"
        >
          <Banknote className="h-3.5 w-3.5 mr-1" /> Penalties {penaltyCount > 0 && `(${penaltyCount})`}
        </Button>
      </div>

      {/* Read/Unread Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="unread" className="rounded-lg min-h-[44px] px-5" data-testid="tab-unread">
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </TabsTrigger>
          <TabsTrigger value="read" className="rounded-lg min-h-[44px] px-5" data-testid="tab-read">
            Reviewed
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Alerts List */}
      {filteredAlerts.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {filter === 'unread' ? 'No Unread Alerts' : 'No Reviewed Alerts'}
            </h3>
            <p className="text-slate-500">
              {filter === 'unread' ? 'All caught up!' : 'Reviewed alerts will appear here'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAlerts.map(alert => {
            const config = alertTypeConfig[alert.type] || alertTypeConfig.urgent_till_alert;
            const Icon = config.icon;
            const isPenaltyType = config.category === 'penalty';

            return (
              <Card 
                key={alert.id}
                className={`border-0 shadow-sm ${!alert.isRead ? 'ring-2 ring-red-200 bg-red-50/50' : ''}`}
                data-testid={`alert-${alert.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${!alert.isRead ? config.iconBg : config.iconBgRead}`}>
                      <Icon className={`h-6 w-6 ${!alert.isRead ? config.iconColor : config.iconColorRead}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{alert.title}</h3>
                            {isPenaltyType && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">Accountability</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                            <Clock className="h-3.5 w-3.5" />
                            <span>{formatTime(alert.createdAt)}</span>
                          </div>
                        </div>
                        {!alert.isRead && (
                          <Badge className="bg-red-100 text-red-800 shrink-0">New</Badge>
                        )}
                      </div>

                      <p className="text-slate-600 mb-3">{alert.body}</p>

                      {/* Shift Info */}
                      {alert.shift && (
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-3 p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>{alert.shift.branchName}</span>
                          </div>
                          <span>-</span>
                          <span>{alert.shift.date}</span>
                          <span>-</span>
                          <span>{alert.shift.scheduledStartTime} - {alert.shift.scheduledEndTime}</span>
                        </div>
                      )}

                      {/* Discrepancy Info (till alerts) */}
                      {alert.discrepancyChecks && alert.discrepancyChecks.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {alert.discrepancyChecks.map(check => (
                            <div 
                              key={check.id}
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                check.discrepancyType === 'shortage' ? 'bg-red-50' : 'bg-amber-50'
                              }`}
                            >
                              <div>
                                <span className={`font-semibold ${
                                  check.discrepancyType === 'shortage' ? 'text-red-700' : 'text-amber-700'
                                }`}>
                                  &pound;{Math.abs(check.discrepancyAmount).toFixed(2)} {check.discrepancyType}
                                </span>
                                <p className="text-sm text-slate-600 mt-0.5">{check.explanation}</p>
                              </div>
                              <Badge className={`${
                                check.status === 'reviewed' ? 'bg-slate-100 text-slate-600' :
                                'bg-amber-100 text-amber-800'
                              }`}>
                                {check.status.replace('_', ' ')}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Penalty Info (penalty alerts) */}
                      {alert.penalties && alert.penalties.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {alert.penalties.map(penalty => (
                            <div key={penalty.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                              <div>
                                <span className="font-semibold text-amber-800">
                                  &pound;{penalty.amount?.toFixed(2)} - {penalty.type?.replace('_', ' ')}
                                </span>
                                <p className="text-sm text-slate-600 mt-0.5">{penalty.reason}</p>
                              </div>
                              <Badge className={`${penaltyStatusStyles[penalty.penaltyStatus] || 'bg-slate-100'} text-xs`}>
                                {penalty.penaltyStatus}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-3">
                        {alert.shift && (
                          <Button
                            variant="outline"
                            className="rounded-full border-2 flex-1"
                            onClick={() => {
                              // Route to the Shift Record detail (iter74 flow):
                              // Alerts are transient; the durable resolution
                              // workspace lives in Shift Records. We still
                              // deep-link to the first open discrepancy via
                              // hash so the manager lands on the exact row.
                              const firstDiscId = alert.discrepancyChecks && alert.discrepancyChecks[0]?.id;
                              const hash = firstDiscId ? `#disc-${firstDiscId}` : '';
                              navigate(`/manager/shift-records/${alert.shift.id}${hash}`, { state: { from: 'alerts' } });
                            }}
                            data-testid={`view-shift-log-${alert.id}`}
                          >
                            {alert.discrepancyChecks && alert.discrepancyChecks.length > 0 ? 'Review discrepancy' : 'View Shift Record'}
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                        {isPenaltyType && alert.worker && (
                          <Button
                            variant="outline"
                            className="rounded-full border-2 flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => navigate('/manager/staff')}
                            data-testid={`view-worker-${alert.id}`}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            View Worker
                          </Button>
                        )}
                        {!alert.isRead && (
                          <Button
                            className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] flex-1"
                            onClick={() => handleMarkRead(alert.id)}
                            data-testid={`mark-read-${alert.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Mark Reviewed
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
